import { FilterDescriptor } from '@progress/kendo-data-query';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { ModelUser, User } from '@module-user/models/model-user';
import { ENUM_USER_STATUS } from '@module-user/enums/enum-user-status';
import { catchError, map } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { Injectable } from '@angular/core';
import { ModelParamsGridGet } from '@models-global/model-get-filters';
import { ModelPagination } from '@models-global/model-pagination';
import { environment } from '@environment/environment';
import { HttpResp } from '@models-global/http-response';
import { Values } from '@utils-global/type-values';

@Injectable()
export class UserService {
  readonly apiList = `${environment.api}/user/all`;
  readonly baseApiUrl = `${environment.api}/user`;

  constructor(private _http: HttpClient) {}

  queryCreate(modelUser: ModelUser): Observable<ModelUser> {
    const opt = {
      headers: new HttpHeaders()
    };
    return this._http
      .post<{ data: ModelUser; message: string }>(
        this.baseApiUrl,
        modelUser.serializeToServer(),
        opt
      )
      .pipe(map((res: { data: ModelUser; message: string }) => res.data));
  }

  queryGetCurrentUser(): Observable<User> {
    return this._http
      .get<HttpResp<User>>(this.baseApiUrl + '/me')
      .pipe(map((response) => response.data));
  }

  queryUpdate(user: ModelUser): Observable<ModelUser> {
    return this._http
      .put<HttpResp<User>>(
        this.baseApiUrl + `/${user.id}`,
        user.serializeToServer()
      )
      .pipe(map((response) => new ModelUser(response.data)));
  }

  updateUserStatus(
    userId: string,
    status: Values<typeof ENUM_USER_STATUS>
  ): Observable<ModelUser> {
    const action =
      status === ENUM_USER_STATUS.ACTIVE ? 'activate' : 'deactivate';
    return this._http
      .post<HttpResp<User>>(this.baseApiUrl + `/${userId}/${action}`, {})
      .pipe(map((response) => new ModelUser(response.data)));
  }

  queryDelete(user: ModelUser): Observable<number> {
    const opt = {
      headers: new HttpHeaders()
    };
    return this._http.delete<number>(this.baseApiUrl + `/${user.id}`, opt);
  }

  queryList(
    data: ModelParamsGridGet<FilterDescriptor[], User>
  ): Observable<ModelPagination<ModelUser[]>> {
    let params = new HttpParams();
    params = params.append('skip', data.skip);
    params = params.append('take', data.take);
    params = params.append('sortBy', data.sortBy);
    params = params.append('orderBy', data.orderBy);
    for (const item of data.filter) {
      const key = item.field as string;
      const value = { operator: item.operator, value: item.value };
      params = params.append(key, JSON.stringify(value));
    }
    const opt = {
      headers: new HttpHeaders(),
      params
    };

    return this._http
      .get<HttpResp<ModelPagination<ModelUser[]>>>(this.apiList, opt)
      .pipe(
        map((response) => {
          const users: User[] = response.data.data ?? [];
          const models = users.map((u) => new ModelUser(u));
          return {
            data: models,
            total: response.data.total,
            skip: response.data.skip,
            take: response.data.take
          };
        })
      );
  }

  public checkEmail(email: string): Observable<boolean> {
    return this._http
      .post<HttpResp<{ exist: boolean }>>(`${this.baseApiUrl}/check-email`, {
        email
      })
      .pipe(
        map((response) => response.data.exist),
        catchError(() => of(false))
      );
  }
}
